import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { resolveBrandAssetSrc } from '@/features/shared/brand/branding-core'
import { useBranding, useShellBrandName } from '@/features/shared/brand/branding-hooks'
import { normalizeLanguage, setAppLanguage, type SupportedLanguage } from '@/i18n/i18n'
import type { PortalKind } from '@/lib/auth/token-storage'
import { cn } from '@/lib/cn'

import type { SidebarNavItem } from './app-shell/navigation-types'
import { ProfileMenu } from './app-shell/profile-menu'
import { logoutPortal } from './app-shell/session-actions'
import { SidebarNavigation } from './app-shell/sidebar-navigation'
import { SidebarUser } from './app-shell/sidebar-user'
import type { AppShellUser } from './app-shell/user-display'

export type {
  SidebarNavChild,
  SidebarNavItem,
  SidebarNavSearchItem,
  SidebarNavSearchMenu,
} from './app-shell/navigation-types'
export type { AppShellUser } from './app-shell/user-display'

export interface AppShellProps {
  activePath?: string
  children: ReactNode
  navigation?: SidebarNavItem[]
  navigationExpandedGroupsPersistenceKey?: string
  portal?: PortalKind
  user: AppShellUser
}

const mobileSidebarQuery = '(max-width: 767px)'
const sidebarCollapsedStorageKeys: Record<PortalKind, string> = {
  admin: 'aihub_admin_sidebar_collapsed',
  user: 'aihub_user_sidebar_collapsed',
}
const legacySidebarCollapsedStorageKeys: Record<PortalKind, string> = {
  admin: 'aiportal_admin_sidebar_collapsed',
  user: 'aiportal_user_sidebar_collapsed',
}

function parseStoredCollapsedState(storedValue: string | null) {
  if (storedValue === 'true') {
    return true
  }

  if (storedValue === 'false') {
    return false
  }

  return null
}

function readStoredCollapsedState(portal: PortalKind) {
  if (typeof window === 'undefined') {
    return null
  }

  try {
    return (
      parseStoredCollapsedState(window.localStorage.getItem(sidebarCollapsedStorageKeys[portal])) ??
      parseStoredCollapsedState(
        window.localStorage.getItem(legacySidebarCollapsedStorageKeys[portal]),
      )
    )
  } catch {
    return null
  }
}

function writeStoredCollapsedState(portal: PortalKind, collapsed: boolean) {
  if (typeof window === 'undefined') {
    return
  }

  try {
    window.localStorage.setItem(sidebarCollapsedStorageKeys[portal], String(collapsed))
    window.localStorage.removeItem(legacySidebarCollapsedStorageKeys[portal])
  } catch {
    // 忽略受限存储环境，折叠状态仍保留在当前 React 状态中。
  }
}

function getInitialCollapsedState(portal: PortalKind) {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return readStoredCollapsedState(portal) ?? false
  }

  if (window.matchMedia(mobileSidebarQuery).matches) {
    return true
  }

  return readStoredCollapsedState(portal) ?? false
}

function ShellBrand({ portal }: { portal: PortalKind }) {
  const brandName = useShellBrandName(portal)
  const {
    settings: { logo },
  } = useBranding()

  return (
    <div className="absolute left-0 right-[70px] top-2.5 grid h-10 grid-cols-[56px_minmax(0,1fr)] items-center">
      <span className="flex size-10 items-center justify-center justify-self-center">
        <img
          alt=""
          className="size-[22px] shrink-0"
          data-testid="shell-brand-logo"
          src={resolveBrandAssetSrc(logo)}
        />
      </span>
      <span
        className="min-w-0 truncate text-[17px] font-bold leading-none text-[var(--color-sidebar-active-text)]"
        data-testid="shell-brand-text"
      >
        {brandName}
      </span>
    </div>
  )
}

function SidebarToggle({
  collapsed,
  onToggle,
  variant,
}: {
  collapsed: boolean
  onToggle: () => void
  variant: 'expanded' | 'collapsed'
}) {
  const { t } = useTranslation('shell')

  return (
    <button
      aria-label={collapsed ? t('sidebar.expand') : t('sidebar.collapse')}
      aria-pressed={collapsed}
      className={cn(
        'group flex size-10 cursor-pointer items-center justify-center rounded-lg text-[var(--color-sidebar-text)] transition-colors duration-200 hover:bg-[var(--color-sidebar-hover)] hover:text-[var(--color-sidebar-active-text)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)]',
        variant === 'expanded' ? 'absolute right-[14px] top-2.5' : 'ml-2 mt-2.5',
      )}
      onClick={onToggle}
      type="button"
    >
      <svg
        aria-hidden
        className="size-5"
        data-testid="sidebar-toggle-icon"
        fill="none"
        viewBox="0 0 20 20"
      >
        <rect
          height="15"
          rx="2.75"
          stroke="currentColor"
          strokeWidth="1.8"
          width="16"
          x="2"
          y="2.5"
        />
        <path d="M7 3.25v13.5" stroke="currentColor" strokeWidth="1.8" />
        <path
          className={cn(
            'origin-center transition-[opacity,transform] duration-150 ease-out motion-reduce:transition-none',
            collapsed
              ? 'translate-x-0 opacity-100 group-hover:translate-x-[1.5px]'
              : 'translate-x-0 opacity-0',
          )}
          d="m11 7.25 2.75 2.75L11 12.75"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.8"
        />
        <path
          className={cn(
            'origin-center transition-[opacity,transform] duration-150 ease-out motion-reduce:transition-none',
            collapsed
              ? 'translate-x-0 opacity-0'
              : 'translate-x-0 opacity-100 group-hover:-translate-x-[1.5px]',
          )}
          d="m14 7.25-2.75 2.75L14 12.75"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.8"
        />
      </svg>
    </button>
  )
}

function useAppShellLanguage() {
  const { i18n } = useTranslation('shell')
  const language = normalizeLanguage(i18n.language)

  const changeLanguage = (nextLanguage: SupportedLanguage) => {
    void setAppLanguage(nextLanguage)
  }

  return {
    changeLanguage,
    language,
  }
}

export function AppShell({
  activePath,
  children,
  navigation = [],
  navigationExpandedGroupsPersistenceKey,
  portal = 'user',
  user,
}: AppShellProps) {
  const { t } = useTranslation('shell')
  const [collapsed, setCollapsed] = useState(() => getInitialCollapsedState(portal))
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const { changeLanguage, language } = useAppShellLanguage()
  const sidebarWidth = collapsed ? 'w-14' : 'w-[min(248px,calc(100vw-32px))] md:w-[248px]'
  const shellUser = useMemo(() => user, [user])
  const mainLabel = portal === 'admin' ? t('adminWorkspace') : undefined
  const toggleCollapsed = useCallback(() => {
    const nextCollapsed = !collapsed

    writeStoredCollapsedState(portal, nextCollapsed)
    setCollapsed(nextCollapsed)
  }, [collapsed, portal])

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      return undefined
    }

    const mediaQuery = window.matchMedia(mobileSidebarQuery)

    const syncWithViewport = (event: MediaQueryListEvent | MediaQueryList) => {
      if (event.matches) {
        setCollapsed(true)
      }
    }

    syncWithViewport(mediaQuery)
    mediaQuery.addEventListener('change', syncWithViewport)

    return () => {
      mediaQuery.removeEventListener('change', syncWithViewport)
    }
  }, [])

  return (
    <div
      className="flex h-dvh overflow-hidden bg-[var(--color-app-bg)] text-[var(--color-text)]"
      data-sidebar-collapsed={collapsed}
    >
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-30 flex h-dvh shrink-0 flex-col overflow-hidden bg-[var(--color-sidebar-bg)] transition-[width] md:relative motion-reduce:duration-0',
          'duration-[220ms] ease-[cubic-bezier(0.22,1,0.36,1)]',
          sidebarWidth,
        )}
      >
        {collapsed ? (
          <SidebarToggle collapsed={collapsed} onToggle={toggleCollapsed} variant="collapsed" />
        ) : (
          <div className="relative h-[60px] shrink-0">
            <ShellBrand portal={portal} />
            <SidebarToggle collapsed={collapsed} onToggle={toggleCollapsed} variant="expanded" />
          </div>
        )}
        <SidebarNavigation
          activePath={activePath}
          collapsed={collapsed}
          expandedGroupsPersistenceKey={navigationExpandedGroupsPersistenceKey}
          items={navigation}
          portal={portal}
        />
        <SidebarUser
          collapsed={collapsed}
          menuOpen={userMenuOpen}
          onToggleMenu={() => {
            setUserMenuOpen((open) => !open)
          }}
          user={shellUser}
        />
      </aside>

      {userMenuOpen ? (
        <ProfileMenu
          currentLanguage={language}
          onLanguageChange={changeLanguage}
          onLogout={() => {
            logoutPortal(portal)
          }}
          portal={portal}
          user={shellUser}
        />
      ) : null}

      <main aria-label={mainLabel} className="h-dvh min-w-0 flex-1 overflow-hidden pl-14 md:pl-0">
        <div className="h-dvh overflow-hidden bg-white md:mt-[7px] md:h-[calc(100dvh-7px)] md:rounded-tl-[24px]">
          {children}
        </div>
      </main>
    </div>
  )
}
